import { useShallow } from 'zustand/react/shallow'

import { AstraTooltip, day, getOnlyDate, humanizeDate } from '~/astra'
import { ButtonTag } from '~/components'
import { selectPendingLocationSchedulesForCell } from '~/store/slices/surgeonSchedulerSlice'
import { useStore } from '~/store/store'
import { Location, PractitionerScheduleLocation } from '~/storm'

import { DatePractitionerCell } from '../../../shared'
import { createGetLocationStateFn, useToggleLocationSchedules } from './useToggleLocationSchedules'

type Props = {
    selectedCells: DatePractitionerCell[]
    scheduleLocations: PractitionerScheduleLocation[]
    location: Location
}

export const LocationTag = ({ scheduleLocations, location, selectedCells }: Props) => {
    const assignedLocations = new Set(scheduleLocations.map(l => l.location_id))
    const pendingLocations = useStore(
        useShallow(state =>
            selectedCells.flatMap(({ date, practitionerId }) =>
                selectPendingLocationSchedulesForCell(
                    practitionerId,
                    getOnlyDate(date)
                )(state).filter(l => l.location_id === location.id && !assignedLocations.has(l.location_id))
            )
        )
    )

    const minimalWithPendingLocations = [...scheduleLocations, ...pendingLocations]
    const getLocationState = createGetLocationStateFn(selectedCells, minimalWithPendingLocations)

    const { toggleLocationSchedules } = useToggleLocationSchedules(selectedCells, scheduleLocations)

    function getTooltip(locationId: Location['id']) {
        if (selectedCells.length !== 1) return

        const practitionerScheduleLocation = scheduleLocations.find(location => location.location_id === locationId)
        if (!practitionerScheduleLocation) return

        return `Sist endret av ${practitionerScheduleLocation.updated_by} ${humanizeDate(day(practitionerScheduleLocation.updated_at))}`
    }

    return (
        <AstraTooltip tooltipContent={getTooltip(location.id)}>
            <ButtonTag
                colorType="room"
                fillType={getLocationState(location.id)}
                isLoading={pendingLocations.length > 0}
                onClick={() => toggleLocationSchedules(location)}
                className="transition-all"
                label={location.room_code}
            />
        </AstraTooltip>
    )
}
